import { AppForm } from '../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';
import { ApplicantAppEntityApplicantWrite, ApplicationAppEntityApplicationRead } from '../../../../../../openapi';
import { Injectable } from '@angular/core';
import { TokenData } from '../../../../../../services/ssession.service';
import { UserDetails } from '../../../../../../store/reducers/user-details.reducer';
import { ProxyPersonForm } from './proxy-person.form';
import { EntityType } from '../../../../../../pipes/entity-type.pipe';

@Injectable()
export class ApplicantForm extends AppForm {
  constructor() {
    super({
      isFiledByProxy: new FormControl(false, {
        validators: [Validators.required],
        nonNullable: true
      }),
      identifierType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      entityType: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      firstName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      lastName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      fatherName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      motherName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      birthDate: new FormControl(null, [
        Validators.required,
        Validators.maxLength(255),
        CustomValidator.maxDate(new Date())
      ]),
      pesel: new FormControl('', { nonNullable: true, validators: [Validators.required, CustomValidator.pesel()] }),
      nip: new FormControl('', { nonNullable: true, validators: [Validators.required, CustomValidator.nip()] }),
      regon: new FormControl('', { nonNullable: true, validators: [CustomValidator.regon()] }),
      phone: new FormControl('', {
        validators: [CustomValidator.phone(), Validators.maxLength(255)],
        nonNullable: true
      }),
      email: new FormControl('', {
        validators: [
          CustomValidator.email(),
          Validators.maxLength(255)
        ],
        nonNullable: true
      }),
      country: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      voivodeship: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      county: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      municipality: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      zipCode: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      postOffice: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      city: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      street: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      buildingNumber: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      apartmentNumber: new FormControl('', Validators.maxLength(255))
    });
    this.isFiledByProxyChange();
  }

  public get entityType(): FormControl {
    return this.get('entityType') as FormControl;
  }

  public get identifierType(): FormControl {
    return this.get('identifierType') as FormControl;
  }

  public get proxyPersonForm(): ProxyPersonForm | null {
    return this.get('proxyPersonForm') as ProxyPersonForm | null;
  }

  public get isFiledByProxy(): FormControl {
    return this.get('isFiledByProxy') as FormControl;
  }

  public isFiledByProxyChange(): void {
    const v = this.isFiledByProxy.value;
    if (v) {
      this.addControl('proxyPersonForm', new ProxyPersonForm());
    } else {
      this.removeControl('proxyPersonForm');
    }
  }

  public get country(): FormControl {
    return this.get('country') as FormControl;
  }

  public get voivodeship(): FormControl {
    return this.get('voivodeship') as FormControl;
  }

  public get county(): FormControl {
    return this.get('county') as FormControl;
  }

  public get municipality(): FormControl {
    return this.get('municipality') as FormControl;
  }

  public get nip(): FormControl {
    return this.get('nip') as FormControl;
  }

  public get regon(): FormControl {
    return this.get('regon') as FormControl;
  }

  public get pesel(): FormControl {
    return this.get('pesel') as FormControl;
  }

  public get lastName(): FormControl {
    return this.get('lastName') as FormControl;
  }

  public get firstName(): FormControl {
    return this.get('firstName') as FormControl;
  }

  public get name(): FormControl {
    return this.get('name') as FormControl;
  }

  public get fatherName(): FormControl {
    return this.get('fatherName') as FormControl;
  }

  public get motherName(): FormControl {
    return this.get('motherName') as FormControl;
  }

  public get birthDate(): FormControl {
    return this.get('birthDate') as FormControl;
  }

  public createApplicantTypeControl(): void {
    this.addControl('applicantType', new FormControl('Osoba prawna', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get applicantType(): FormControl | null {
    return this.get('applicantType') as FormControl | null;
  }

  public override patchFormValues(value: ApplicationAppEntityApplicationRead | null): void {
    this.isFiledByProxy.setValue(!!value?.isFiledByProxy);
    this.isFiledByProxyChange();
    this.proxyPersonForm?.patchFormValues(value);

    if (!!value?.applyingAs) {
      this.entityType.setValue(value?.applyingAs === 'Osoba fizyczna' ? EntityType.OSOBA_FIZYCZNA : EntityType.OSOBA_PRAWNA);
    }

    if (this.entityType.value === EntityType.OSOBA_PRAWNA) {
      this.createApplicantTypeControl();
      this.identifierType.setValue('NIP');
    } else {
      this.identifierType.setValue('PESEL');
    }

    const applicant = value?.applicants?.at(0);
    if (applicant) {
      super.patchFormValues({
        entityType: applicant.entityType?.id || '',
        applicantType: applicant?.applicantType,
        identifierType: applicant?.identifierType || 'PESEL',
        country: applicant?.country || '',
        voivodeship: applicant?.voivodeship || '',
        county: applicant?.county || '',
        municipality: applicant?.municipality || '',
        name: applicant?.name || '',
        firstName: applicant?.firstName || '',
        lastName: applicant?.lastName || '',
        motherName: applicant?.motherName || '',
        fatherName: applicant?.fatherName || '',
        birthDate: applicant?.birthDate || null,
        nip: applicant?.nip || '',
        pesel: applicant?.pesel || '',
        regon: applicant?.regon || '',
        city: applicant.city,
        email: applicant.email,
        apartmentNumber: applicant.apartmentNumber,
        phone: applicant?.phoneNumber,
        phone2: applicant?.phoneNumber2,
        buildingNumber: applicant?.houseNumber,
        postOffice: applicant.postOffice,
        zipCode: applicant.postalCode,
        street: applicant.street
      });
    }
  }

  public toWriteModel(): ApplicantAppEntityApplicantWrite {
    return {
      entityType: this.value.entityType,
      applicantType: this.value?.applicantType || null,
      identifierType: this.value.identifierType,
      pesel: this.value?.pesel || null,
      nip: this.value?.nip || null,
      regon: this.value?.regon || null,
      name: this.value?.name || null,
      firstName: this.value?.firstName || null,
      lastName: this.value?.lastName || null,
      motherName: this.value?.motherName || '',
      fatherName: this.value?.fatherName || '',
      birthDate: this.value?.birthDate || null,
      phoneNumber: this.value.phone,
      email: this.value.email,
      city: this.value.city,
      houseNumber: this.value.buildingNumber,
      postalCode: this.value.zipCode,
      postOffice: this.value.postOffice,
      apartmentNumber: this.value.apartmentNumber,
      phoneNumber2: this.value.phone2,
      street: this.value.street,
      country: this.value?.country || null,
      voivodeship: this.value?.voivodeship || null,
      county: this.value?.county || null,
      municipality: this.value?.municipality || null
    };
  }

  public readApplicantDataFromPortal(v: UserDetails | null): void {
    this.patchValue({
      firstName: v?.firstName || '',
      lastName: v?.lastName || '',
      email: v?.email || '',
      phone: v?.phoneNumber || '',
      city: v?.city,
      buildingNumber: v?.houseNumber,
      apartmentNumber: v?.apartmentNumber || '',
      zipCode: v?.postalCode || '',
      postOffice: v?.postOffice || '',
      street: v?.street,
      country: v?.country || '',
      voivodeship: v?.voivodeship || '',
      county: v?.county || '',
      municipality: v?.municipality || ''
    });
  }

  public readApplicantDataFromGovPl(v: TokenData | null): void {
    this.patchValue({
      firstName: v?.firstName || '',
      lastName: v?.lastName || '',
      email: v?.email || '',
      city: '',
      buildingNumber: '',
      apartmentNumber: '',
      zipCode: '',
      postOffice: '',
      street: '',
      pesel: v?.pesel
    });
  }
}
